import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

export type AppConfigValue =
  | boolean
  | number
  | string
  | null
  | AppConfigValue[]
  | { [key: string]: AppConfigValue }

export interface AdminAppTag {
  id: string
  name: string
}

export type ApplicationChannel = 'web_standard' | 'web_wecom' | 'im'

export const applicationChannels: ApplicationChannel[] = ['web_standard', 'web_wecom', 'im']

export interface AdminAppTypeConfigField {
  description: string
  field_type: string
  is_sensitive: boolean
  key: string
  label: string
  options: Record<string, string>[] | null
  placeholder: string
  required: boolean
  validation_pattern: string
}

export interface AdminApp {
  allowed_channels?: ApplicationChannel[]
  app_icon?: string | null
  app_type: string
  avatar?: string | null
  config?: Record<string, AppConfigValue>
  created_at: string
  description: string
  distribution_mode?: 'automatic' | 'manual'
  id: string
  masked_config?: Record<string, AppConfigValue> | null
  name: string
  status: string
  tags?: AdminAppTag[]
  updated_at: string
  usage_instructions?: string | null
  version?: string | null
}

export interface AdminAppIcon {
  app_icon: string
  avatar: string
  file_id: string
  file_path: string
  file_size: number
  mime_type: string | null
}

export type AdminAppTransitionAction =
  | 'approve_for_testing'
  | 'request_publish'
  | 'publish'
  | 'return_to_draft'
  | 'return_to_testing'
  | 'ban'
  | 'republish'

export interface AdminAppsListParams {
  appType?: string
  order?: 'asc' | 'desc'
  orderBy?: string
  page: number
  pageSize: number
  search?: string
  status?: string | string[]
}

export interface AdminAppType {
  config_fields?: AdminAppTypeConfigField[]
  label: string
  type: string
}

export interface AdminAppTypeConfig {
  config_fields: AdminAppTypeConfigField[]
  default_config: Record<string, AppConfigValue>
  type: string
}

export interface AdminAppFormValues {
  appType: string
  avatar?: string | null
  config?: Record<string, AppConfigValue>
  description: string
  distributionMode: 'automatic' | 'manual'
  name: string
  newTags?: string[]
  tagIds?: string[]
  version: string
}

export interface AppOidcClientScopeAssignment {
  assignment_type: 'default' | 'optional'
  client_scope_id: string
  display_name: string
  scope_name: string
  system: boolean
}

export interface AppOidcClient {
  application_ids: string[]
  client_id: string
  client_scope_assignments: AppOidcClientScopeAssignment[]
  default_client_scope_ids: string[]
  id: string
  name: string
  optional_client_scope_ids: string[]
  redirect_uris: string[]
  status: 'active' | 'inactive'
  token_endpoint_auth_method: 'client_secret_basic' | 'client_secret_post'
}

export interface AppOidcConfiguration {
  client: AppOidcClient | null
  enabled: boolean
}

export interface AppOidcConfigurationPayload {
  client_record_id: string | null
}

export interface AdminCompany {
  ad_guid?: string
  code?: string
  created_at?: string
  deleted_at?: string | null
  id: string
  name: string
  status?: number
  type?: string
  updated_at?: string
}

export interface AdminAppMemberTreeNode {
  children?: AdminAppMemberTreeNode[]
  direct_binding?: boolean
  email?: string
  excluded?: boolean
  has_permission?: boolean
  id: string
  is_expanded?: boolean
  job_title?: string
  member_count?: number
  name: string
  type: 'company' | 'department' | 'member' | 'subdepartment'
}

export interface AdminAppMemberBatchResult {
  failed: { account_id: string; error: string }[]
  failed_count: number
  succeeded: { account_id: string; member_id: string }[]
  success_count: number
  total: number
}

export interface AdminAppTransition {
  action: AdminAppTransitionAction
  comment: string | null
  created_at: string | null
  from_status: string
  id: string
  performed_by: string
  performer_name: string
  to_status: string
}

export interface AdminAppTransitionResult {
  action: AdminAppTransitionAction
  id: string
  name: string
  previous_status: string
  status: string
}

export interface TencentAdpValidationStage {
  elapsed_ms: number
  key: string
  message: string
  status: string
}

export interface TencentAdpValidationResult {
  app_id?: string | null
  app_name?: string | null
  app_status?: string | null
  message: string
  ok: boolean
  request_id?: string | null
  stages: TencentAdpValidationStage[]
}

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

interface MessageEnvelope {
  code: 'success'
  message: string
}

interface AdminAppsListData {
  items: AdminApp[]
  page: number
  page_size: number
  total: number
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('admin'),
  portal: 'admin',
})

function appendOptionalQuery(params: URLSearchParams, key: string, value: string | undefined) {
  const normalized = value?.trim()

  if (normalized) {
    params.set(key, normalized)
  }
}

function appendOptionalQueries(
  params: URLSearchParams,
  key: string,
  value: string | string[] | undefined,
) {
  if (Array.isArray(value)) {
    value
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach((item) => params.append(key, item))
    return
  }

  appendOptionalQuery(params, key, value)
}

export async function listAdminApps({
  appType,
  order = 'desc',
  orderBy = 'name',
  page,
  pageSize,
  search,
  status,
}: AdminAppsListParams) {
  const params = new URLSearchParams({
    order,
    order_by: orderBy,
    page: String(page),
    page_size: String(pageSize),
  })

  appendOptionalQuery(params, 'search', search)
  appendOptionalQueries(params, 'status', status)
  appendOptionalQuery(params, 'app_type', appType)

  const response = await apiClient.request<SuccessEnvelope<AdminAppsListData>>(
    `/admin/app?${params.toString()}`,
  )

  return response.data
}

export async function listAdminAppTypes() {
  const response = await apiClient.request<SuccessEnvelope<AdminAppType[]>>('/admin/app/types')

  return response.data
}

export async function getAdminAppDetail(appId: string) {
  const response = await apiClient.request<SuccessEnvelope<AdminApp>>(
    `/admin/app/${encodeURIComponent(appId)}`,
  )

  return response.data
}

export async function getAdminAppTypeConfig(appType: string) {
  const response = await apiClient.request<SuccessEnvelope<AdminAppTypeConfig>>(
    `/admin/app/types/${encodeURIComponent(appType)}/config`,
  )

  return response.data
}

export async function validateTencentAdpAppConfig({
  appId,
  config,
}: {
  appId?: string | null
  config: Record<string, AppConfigValue>
}) {
  const path = appId
    ? `/admin/app/${encodeURIComponent(appId)}/adp/validate-config`
    : '/admin/app/adp/validate-config'
  const response = await apiClient.request<SuccessEnvelope<TencentAdpValidationResult>>(path, {
    body: JSON.stringify({ config }),
    method: 'POST',
  })

  return response.data
}

export async function listAdminAppTagOptions() {
  const response = await apiClient.request<SuccessEnvelope<{ items: AdminAppTag[] }>>(
    '/admin/app-tags?page=1&page_size=200',
  )

  return response.data.items
}

function trimmedOrUndefined(value: string) {
  const normalized = value.trim()

  return normalized.length > 0 ? normalized : undefined
}

function normalizedTagNames(values: string[] | undefined) {
  const normalizedValues = (values ?? [])
    .map((value) => value.trim().replace(/\s+/g, ' '))
    .filter(Boolean)

  return Array.from(new Set(normalizedValues))
}

function appPayload(values: AdminAppFormValues) {
  const avatar = values.avatar?.trim()

  return {
    app_type: values.appType,
    avatar: avatar !== undefined && avatar.length > 0 ? avatar : undefined,
    config: values.config ?? {},
    description: values.description.trim(),
    distribution_mode: values.distributionMode,
    name: values.name.trim(),
    new_tags: normalizedTagNames(values.newTags),
    tag_ids: values.tagIds ?? [],
    version: trimmedOrUndefined(values.version),
  }
}

export async function getAdminAppNameAvailability(name: string, excludeAppId?: string | null) {
  const params = new URLSearchParams({ name: name.trim() })
  if (excludeAppId) params.set('exclude_app_id', excludeAppId)

  const response = await apiClient.request<SuccessEnvelope<Record<string, unknown>>>(
    `/admin/app/name-availability?${params.toString()}`,
  )

  if (
    typeof response.data.available !== 'boolean' ||
    typeof response.data.normalized_name !== 'string'
  ) {
    throw new Error('Invalid app name availability response')
  }

  return {
    available: response.data.available,
    normalized_name: response.data.normalized_name,
  }
}

export async function createAdminApp(values: AdminAppFormValues) {
  const response = await apiClient.request<SuccessEnvelope<AdminApp>>('/admin/app', {
    body: JSON.stringify(appPayload(values)),
    method: 'POST',
  })

  return response.data
}

export async function updateAdminApp(appId: string, values: AdminAppFormValues) {
  const response = await apiClient.request<SuccessEnvelope<AdminApp>>(
    `/admin/app/${encodeURIComponent(appId)}`,
    {
      body: JSON.stringify(appPayload(values)),
      method: 'PUT',
    },
  )

  return response.data
}

export async function updateAdminAppChannels(appId: string, allowedChannels: ApplicationChannel[]) {
  const response = await apiClient.request<SuccessEnvelope<AdminApp>>(
    `/admin/app/${encodeURIComponent(appId)}`,
    {
      body: JSON.stringify({ allowed_channels: allowedChannels }),
      method: 'PUT',
    },
  )
  return response.data
}

export async function updateAdminAppAvatar(appId: string, avatar: string) {
  const response = await apiClient.request<SuccessEnvelope<AdminAppIcon>>(
    `/admin/app/${encodeURIComponent(appId)}/avatar`,
    {
      body: JSON.stringify({ avatar }),
      method: 'PUT',
    },
  )

  return response.data
}

export async function getAdminAppOidcConfiguration(appId: string) {
  const response = await apiClient.request<SuccessEnvelope<AppOidcConfiguration>>(
    `/admin/app/${encodeURIComponent(appId)}/oidc`,
  )
  return response.data
}

export async function updateAdminAppOidcConfiguration(
  appId: string,
  values: AppOidcConfigurationPayload,
) {
  const response = await apiClient.request<SuccessEnvelope<AppOidcConfiguration>>(
    `/admin/app/${encodeURIComponent(appId)}/oidc`,
    { body: JSON.stringify(values), method: 'PUT' },
  )
  return response.data
}

export async function listAdminOidcClients() {
  const response =
    await apiClient.request<SuccessEnvelope<{ items: AppOidcClient[] }>>('/admin/idp/clients')
  return response.data.items
}

export async function deleteAdminApp(appId: string) {
  await apiClient.request<MessageEnvelope>(`/admin/app/${encodeURIComponent(appId)}`, {
    method: 'DELETE',
  })
}

export async function getAdminAppAvailableActions(appId: string) {
  const response = await apiClient.request<
    SuccessEnvelope<{ actions: AdminAppTransitionAction[] }>
  >(`/admin/app/${encodeURIComponent(appId)}/available-actions`)

  return response.data.actions
}

export async function listAdminAppTransitions(appId: string) {
  const response = await apiClient.request<SuccessEnvelope<{ items: AdminAppTransition[] }>>(
    `/admin/app/${encodeURIComponent(appId)}/transitions`,
  )

  return response.data.items
}

export async function transitionAdminAppStatus({
  action,
  appId,
  comment,
}: {
  action: AdminAppTransitionAction
  appId: string
  comment?: string
}) {
  const normalizedComment = comment?.trim()
  const response = await apiClient.request<SuccessEnvelope<AdminAppTransitionResult>>(
    `/admin/app/${encodeURIComponent(appId)}/transition`,
    {
      body: JSON.stringify({
        action,
        comment: normalizedComment && normalizedComment.length > 0 ? normalizedComment : undefined,
      }),
      method: 'POST',
    },
  )

  return response.data
}

export async function listAdminCompanies() {
  const response = await apiClient.request<
    SuccessEnvelope<{
      data: AdminCompany[]
      pagination: {
        page: number
        page_size: number
        total: number
        total_page: number
      }
    }>
  >('/admin/authz/companies')

  return response.data.data
}

export async function getAdminAppMembersPermissionTree(appId: string, companyId: string) {
  const response = await apiClient.request<SuccessEnvelope<AdminAppMemberTreeNode>>(
    `/admin/app/${encodeURIComponent(appId)}/members/${encodeURIComponent(companyId)}`,
  )

  return response.data
}

export async function batchBindAdminAppMembers(appId: string, accountIds: string[]) {
  const response = await apiClient.request<SuccessEnvelope<AdminAppMemberBatchResult>>(
    '/admin/app-member/batch-bind',
    {
      body: JSON.stringify({
        account_ids: accountIds,
        app_id: appId,
        permission: 'read_write',
        role: 'user',
      }),
      method: 'POST',
    },
  )

  return response.data
}

export async function batchUnbindAdminAppMembers(appId: string, accountIds: string[]) {
  const response = await apiClient.request<SuccessEnvelope<AdminAppMemberBatchResult>>(
    '/admin/app-member/batch-unbind',
    {
      body: JSON.stringify({
        account_ids: accountIds,
        app_id: appId,
      }),
      method: 'POST',
    },
  )

  return response.data
}

export async function createOrUpdateAdminAppMember({
  accountId,
  appId,
  excluded,
}: {
  accountId: string
  appId: string
  excluded: boolean
}) {
  const response = await apiClient.request<
    SuccessEnvelope<{
      account_id: string
      app_id: string
      excluded: boolean
      id: string
      permission: string
      role: string
    }>
  >('/admin/app-member/bind', {
    body: JSON.stringify({
      account_id: accountId,
      app_id: appId,
      excluded,
      permission: 'read_write',
      role: 'user',
    }),
    method: 'POST',
  })

  return response.data
}

export async function bindAdminAppDepartment(appId: string, orgUnitId: string) {
  const response = await apiClient.request<
    SuccessEnvelope<{
      app_id: string
      id: string
      org_unit_id: string
      org_unit_name: string
      permission: string
      role: string
    }>
  >(`/admin/app-member/${encodeURIComponent(appId)}/departments`, {
    body: JSON.stringify({
      org_unit_id: orgUnitId,
      permission: 'read_write',
      role: 'user',
    }),
    method: 'POST',
  })

  return response.data
}

export async function unbindAdminAppDepartment(appId: string, orgUnitId: string) {
  const response = await apiClient.request<MessageEnvelope>(
    `/admin/app-member/${encodeURIComponent(appId)}/departments/${encodeURIComponent(orgUnitId)}`,
    {
      method: 'DELETE',
    },
  )

  return response
}
